import {inject, Injectable} from '@angular/core';
import {BdsmPractice} from '@models';
import {MarkdownContent, MarkdownContentService} from './markdown-content.service';
import {TranslateHelperService} from './translate-helper.service';
@Injectable({
	           providedIn: 'root'
           })
export class BdsmPracticesService {
	private readonly markdownService = inject(MarkdownContentService);
	private readonly translateService = inject(TranslateHelperService);

	private readonly practiceSlugs = [
		'aftercare', 'age-play', 'bondage', 'brat', 'brat-tamer', 'breath-play',
		'cages', 'cbt', 'chastete', 'collar', 'discipline', 'domination', 'teasing',
		'edging', 'electrostimulation', 'exhibitionisme', 'figging', 'findom', 'fire-play',
		'fisting', 'foot-worship', 'forced-orgasm', 'fouet', 'gag', 'humiliation', 'hypnose',
		'ice-play', 'impact-play', 'knife-play', 'marking', 'masochisme', 'medical-play',
		'mummification', 'needle-play', 'orgasm-control', 'pegging', 'pet-play', 'rope-bondage',
		'sadisme', 'safeword', 'sensation-play', 'sensory-deprivation', 'service-sub',
		'soumission', 'spanking', 'suspension', 'switch', 'trampling', 'voyeurisme',
		'watersports', 'wax-play'
	];

	async getAllPractices(): Promise<BdsmPractice[]> {
		const currentLang = this.translateService.currentLang || 'en';
		const results = await Promise.all(this.practiceSlugs.map(async (slug) => {
			try {
				const content = await this.markdownService.loadPracticeContent(slug, currentLang);
				return this.createPracticeFromMetadata(slug, content.metadata);
			} catch {
				return null;
			}
		}));

		return results
			.filter((practice): practice is BdsmPractice => practice !== null)
			.sort((a, b) => a.name.localeCompare(b.name));
	}

	async getPracticeBySlug(slug: string): Promise<BdsmPractice | undefined> {
		const currentLang = this.translateService.currentLang || 'en';
		try {
			const content = await this.markdownService.loadPracticeContent(slug, currentLang);
			return this.createPracticeFromMetadata(slug, content.metadata);
		} catch {
			return undefined;
		}
	}

	async getPracticesByCategory(category: string): Promise<BdsmPractice[]> {
		const practices = await this.getAllPractices();
		return practices.filter(practice => practice.category === category);
	}

	async getCategories(): Promise<string[]> {
		const practices = await this.getAllPractices();
		return [...new Set(practices.map(practice => practice.category))];
	}

	async searchPractices(query: string): Promise<BdsmPractice[]> {
		const lowerQuery = query.toLowerCase();
		const practices = await this.getAllPractices();
		return practices.filter(practice =>
			practice.name.toLowerCase().includes(lowerQuery) ||
			practice.shortDescription.toLowerCase().includes(lowerQuery) ||
			practice.keywords?.toLowerCase().includes(lowerQuery)
		);
	}

	private createPracticeFromMetadata(slug: string, metadata?: MarkdownContent['metadata']): BdsmPractice {
		return {
			id: slug,
			slug: slug,
			name: metadata?.title || slug,
			shortDescription: metadata?.description || '',
			category: metadata?.category || '',
			keywords: metadata?.keywords
		};
	}
}

